import { useRef, useState } from "react";
import { UploadCloud, FileText, X } from "lucide-react";
import { Button } from "@/components/ui/button";

const FileDropzone = ({ files, onChange, accept = ".pdf", disabled }) => {
  const inputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);

  const handleFiles = (incoming) => {
    if (!incoming || incoming.length === 0) return;
    const arr = Array.from(incoming).filter((f) =>
      f.type === "application/pdf" || f.name.toLowerCase().endsWith(".pdf")
    );
    // Aynı isimde dosya varsa tekrar eklemeyi engelle
    const existing = new Set(files.map((f) => `${f.name}-${f.size}`));
    const merged = [...files];
    arr.forEach((f) => {
      const key = `${f.name}-${f.size}`;
      if (!existing.has(key)) {
        merged.push(f);
        existing.add(key);
      }
    });
    onChange(merged);
  };

  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (disabled) return;
    handleFiles(e.dataTransfer.files);
  };

  const onDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) setDragActive(true);
  };

  const onDragLeave = (e) => {
    e.preventDefault();
    setDragActive(false);
  };

  const removeFile = (idx) => {
    const next = files.filter((_, i) => i !== idx);
    onChange(next);
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  return (
    <div className="space-y-4">
      <div
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onClick={() => !disabled && inputRef.current?.click()}
        role="button"
        tabIndex={0}
        data-testid="file-dropzone"
        className={`border-2 border-dashed rounded-xl p-10 flex flex-col items-center justify-center text-center transition-all cursor-pointer
          ${
            dragActive
              ? "border-blue-500 bg-blue-50"
              : "border-slate-300 bg-slate-50 hover:bg-slate-100 hover:border-blue-400"
          }
          ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}
      >
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          multiple
          hidden
          disabled={disabled}
          onChange={(e) => handleFiles(e.target.files)}
          data-testid="file-input"
        />
        <div className="w-12 h-12 rounded-full bg-white border border-slate-200 flex items-center justify-center mb-4 shadow-sm">
          <UploadCloud className="w-6 h-6 text-blue-600" />
        </div>
        <p className="text-base font-medium text-slate-900">
          PDF dosyalarını sürükleyin veya seçmek için tıklayın
        </p>
        <p className="text-sm text-slate-500 mt-1">
          Birden fazla PDF dosyası yükleyebilirsiniz
        </p>
      </div>

      {files.length > 0 && (
        <div className="space-y-2" data-testid="file-list">
          <div className="text-xs font-medium uppercase tracking-widest text-slate-500">
            Seçilen Dosyalar ({files.length})
          </div>
          <ul className="divide-y divide-slate-200 border border-slate-200 rounded-lg bg-white">
            {files.map((file, idx) => (
              <li
                key={`${file.name}-${idx}`}
                className="flex items-center justify-between px-4 py-3"
                data-testid={`file-item-${idx}`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-9 h-9 rounded-md bg-red-50 text-red-600 flex items-center justify-center shrink-0">
                    <FileText className="w-4 h-4" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-900 truncate">
                      {file.name}
                    </div>
                    <div className="text-xs text-slate-500">
                      {formatSize(file.size)}
                    </div>
                  </div>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeFile(idx);
                  }}
                  disabled={disabled}
                  className="text-slate-500 hover:text-red-600"
                  data-testid={`remove-file-${idx}`}
                >
                  <X className="w-4 h-4" />
                </Button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default FileDropzone;
